import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { IBusinessAttribute } from 'app/business-attributes/business-attribute.model';
import { BusinessAttributesService } from 'app/business-attributes/business-attributes.service';
import { ErrorHandler } from 'app/shared/service/error-handler.service';

@Component({
  selector: 'cc-company-settings-business-attributes-selector',
  templateUrl: './company-settings-business-attributes-selector.component.html',
  styleUrls: ['./company-settings-business-attributes-selector.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class CompanySettingsBusinessAttributesSelectorComponent implements OnChanges {
  selectedVertical: IBusinessAttribute;
  selectedRole: IBusinessAttribute;

  companyId: string;
  verticalOptions: IBusinessAttribute[];
  roleOptions: IBusinessAttribute[];

  readonly otherOption = {
    _id: 'other',
    code: 'other',
    name: 'Other',
  } as IBusinessAttribute;

  @Input() businessAttributes: string[];
  @Input() disabled: boolean;
  @Output() onSelection = new EventEmitter<IBusinessAttribute[]>();
  @Output() onLoad = new EventEmitter<boolean>();

  readonly selectPlaceholder = 'Select...';

  private destroy$ = new Subject<void>();

  constructor(
    private readonly errorHandler: ErrorHandler,
    private readonly businessAttributesService: BusinessAttributesService,
    protected readonly route: ActivatedRoute,
    protected readonly ref: ChangeDetectorRef,
  ) {}

  ngOnChanges() {
    this.loadVerticals();
    this.loadRoles();
  }

  private loadRoles(): void {
    this.businessAttributesService
      .getRoles()
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (roles: IBusinessAttribute[]) => {
          this.roleOptions = roles;

          this.roleOptions.push(this.otherOption);

          this.selectedRole =
            roles.find((role) => this.businessAttributes?.some((ba) => ba === role._id)) || this.otherOption;

          this.emitBusinessAttributesLoaded();
        },
        (error) => {
          this.emitBusinessAttributesLoaded();
          this.errorHandler.handle(error);
        },
      );
  }

  private loadVerticals(): void {
    this.businessAttributesService
      .getVerticals()
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (verticals: IBusinessAttribute[]) => {
          this.verticalOptions = verticals?.map((vertical) => {
            vertical.name = vertical.description;
            return vertical;
          });

          this.verticalOptions.push(this.otherOption);

          this.selectedVertical =
            verticals.find((vertical) => this.businessAttributes?.some((ba) => ba === vertical._id)) ||
            this.otherOption;

          this.emitBusinessAttributesLoaded();
        },
        (error) => {
          this.emitBusinessAttributesLoaded();
          this.errorHandler.handle(error);
        },
      );
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSelectionChange(): void {
    const result = [];

    if (this.selectedVertical?._id !== this.otherOption._id) {
      result.push(this.selectedVertical);
    }

    if (this.selectedRole?._id !== this.otherOption._id) {
      result.push(this.selectedRole);
    }

    this.onSelection.emit(result);
  }

  private emitBusinessAttributesLoaded() {
    this.onLoad.emit(true);
  }
}
